import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, 
  CheckCircle, Circle, MessageCircle, Instagram, Linkedin,
  Clock, Target, Users, TrendingUp, Heart, AlertCircle,
  Plus, X, Edit2, Save, Trash2
} from 'lucide-react';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default 30-day media agenda template
const defaultAgendaTemplate = {
  week1: {
    title: "Week 1: Awareness & Introduction",
    days: [
      {
        day: "Monday",
        hasPost: true,
        postTitle: "Post 1 (Carousel)",
        engagementActions: [
          "Respond to every comment (even with just 👍 or ❤️)",
          "Ask in Stories: \"Have you ever gotten a second opinion? Share your experience in our DMs.\"",
          "Comment on 5–10 posts from: #PatientAdvocate, #BreastCancerAwareness, #HeartHealth"
        ]
      },
      {
        day: "Tuesday",
        hasPost: false,
        postTitle: null,
        engagementActions: [
          "Story Poll: \"Which is harder: getting a diagnosis or understanding it?\"",
          "DM 3–5 followers who liked your post: \"Thanks for the like! Are you exploring second opinions for yourself or a loved one?\"",
          "Engage with 10 posts under #MedicalSecondOpinion"
        ]
      },
      {
        day: "Wednesday",
        hasPost: true,
        postTitle: "Post 2 (Statistic)",
        engagementActions: [
          "Reply to comments with: \"It's a staggering number, isn't it? We're here to help reduce it.\"",
          "Story Q&A: \"What's your biggest fear about medical errors? Ask us anything.\"",
          "Like/comment on posts from @PatientsRights & @MedAdvocacy accounts"
        ]
      },
      {
        day: "Thursday",
        hasPost: false,
        postTitle: null,
        engagementActions: [
          "Share a user's comment (with permission) in Stories + your response",
          "DM 2–3 people who engaged: \"We appreciate your engagement! Here's a helpful article on patient safety [link].\"",
          "Engage with 5 cardiology-related posts (#CardioTwitter)"
        ]
      },
      {
        day: "Friday",
        hasPost: true,
        postTitle: "Post 3 (Reel)",
        engagementActions: [
          "Reply to comments with: \"Which step resonates most with you?\"",
          "Story Sticker: \"Add Yours\" — \"My health win this week was…\"",
          "Comment on 5 oncology-related reels with supportive messages"
        ]
      },
      {
        day: "Saturday/Sunday",
        hasPost: false,
        postTitle: "Community Building",
        engagementActions: [
          "Share 1 inspirational health quote in Stories",
          "Reply to all weekend DMs (even if just \"We'll get back to you Monday!\")",
          "Engage with 10 family caregiver posts (#CaregiverSupport)"
        ]
      }
    ]
  },
  week2: {
    title: "Week 2: Value & Education",
    days: [
      {
        day: "Monday",
        hasPost: true,
        postTitle: "Post 4 (Carousel)",
        engagementActions: [
          "Ask in comments: \"Which step do you wish was easier in healthcare?\"",
          "Story Quiz: \"How long does a CADENCE review take? A) 1 week B) 24–48 hrs C) 1 month\"",
          "Engage with 5 medical professional accounts (@Oncologists, @CardioNews)"
        ]
      },
      {
        day: "Tuesday",
        hasPost: false,
        postTitle: null,
        engagementActions: [
          "DM 3 followers who saved your post: \"We saw you saved our process post — would you like a free guide on preparing for a second opinion?\"",
          "Comment on 8–10 posts under #HealthLiteracy",
          "Share a behind-the-scenes Story (e.g., \"Our team reviewing literature today\")"
        ]
      },
      {
        day: "Wednesday",
        hasPost: true,
        postTitle: "Post 5 (Military Precision)",
        engagementActions: [
          "Reply to comments with: \"Precision matters. How can we bring more clarity to your health journey?\"",
          "Story Question: \"What does 'trust in healthcare' mean to you?\"",
          "Engage with veterans' health accounts (@VeteransHealth)"
        ]
      },
      {
        day: "Thursday",
        hasPost: false,
        postTitle: null,
        engagementActions: [
          "Run a Story Poll: \"Would you attend a free webinar on navigating complex diagnoses? Yes/No\"",
          "DM 2 people who voted: \"Thanks for voting! We'll keep you posted.\"",
          "Engage with 10 posts under #Neurology"
        ]
      },
      {
        day: "Friday",
        hasPost: true,
        postTitle: "Post 6 (Testimonial)",
        engagementActions: [
          "Ask in comments: \"What would clarity in your diagnosis look like for you?\"",
          "Share another testimonial in Stories",
          "Engage with cancer survivor accounts"
        ]
      },
      {
        day: "Saturday/Sunday",
        hasPost: false,
        postTitle: "Connection",
        engagementActions: [
          "Share a \"Myth vs. Fact\" about second opinions in Stories",
          "Reply to weekend comments with personalized responses",
          "Engage with 5 health coach accounts and offer collaboration"
        ]
      }
    ]
  },
  week3: {
    title: "Week 3: Trust & Social Proof",
    days: [
      {
        day: "Monday",
        hasPost: true,
        postTitle: "Post 7 (Carousel)",
        engagementActions: [
          "Ask in comments: \"Tag someone who needs to see this.\"",
          "Story \"Add Yours\": \"My support system includes…\"",
          "Engage with 10 posts under #FamilyCaregiver"
        ]
      },
      {
        day: "Tuesday",
        hasPost: false,
        postTitle: null,
        engagementActions: [
          "DM 3–5 engaged followers: \"We're launching a private Facebook group for patient advocates — interested in joining?\"",
          "Comment on 5 medical podcast posts (@HealthcarePodcast)",
          "Share a \"Question of the Day\" in Stories"
        ]
      },
      {
        day: "Wednesday",
        hasPost: true,
        postTitle: "Post 8 (Reel)",
        engagementActions: [
          "Reply to comments with: \"We're glad you found this helpful! Want to see more behind-the-scenes?\"",
          "Story Sticker: \"Question Box\" — \"What part of our process are you most curious about?\"",
          "Engage with telemedicine accounts (@Teladoc, @Amwell)"
        ]
      },
      {
        day: "Thursday",
        hasPost: false,
        postTitle: null,
        engagementActions: [
          "Share a user's Q&A question (with answer) in Stories",
          "DM 2 people who shared your reel: \"Thank you for sharing! Here's a $50 credit toward your first consultation.\"",
          "Engage with 8 posts under #MedicalTransparency"
        ]
      },
      {
        day: "Friday",
        hasPost: true,
        postTitle: "Post 9 (Graphic)",
        engagementActions: [
          "Ask in comments: \"Which question is most important to you?\"",
          "Story Poll: \"Have you ever asked your doctor these questions? Yes/No\"",
          "Engage with patient safety accounts (@PatientSafety)"
        ]
      },
      {
        day: "Saturday/Sunday",
        hasPost: false,
        postTitle: "Authority Building",
        engagementActions: [
          "Share a LinkedIn article you wrote in Stories",
          "Engage with 10 healthcare influencer posts",
          "Reply to all weekend DMs with warm, helpful tone"
        ]
      }
    ]
  },
  week4: {
    title: "Week 4: Conversion & Community",
    days: [
      {
        day: "Monday",
        hasPost: true,
        postTitle: "Post 10 (Carousel)",
        engagementActions: [
          "Ask in comments: \"Which CADENCE value speaks to you most?\"",
          "Story Countdown: \"2 days until our free webinar sign-up closes!\"",
          "Engage with 5 healthcare admin accounts (@MedicalDirectors)"
        ]
      },
      {
        day: "Tuesday",
        hasPost: false,
        postTitle: null,
        engagementActions: [
          "DM 5 people who engaged with multiple posts: \"We'd love to hear your story. Would you be open to a brief interview?\"",
          "Comment on 8 posts under #HealthAdvocacy",
          "Share a \"Tip of the Day\" in Stories"
        ]
      },
      {
        day: "Wednesday",
        hasPost: true,
        postTitle: "Post 11 (Graphic)",
        engagementActions: [
          "Reply to comments with: \"Speed + accuracy = peace of mind. Ready to get started?\"",
          "Story \"Swipe Up\" (if available): Link to booking page",
          "Engage with 10 posts under #MedicalEfficiency"
        ]
      },
      {
        day: "Thursday",
        hasPost: false,
        postTitle: null,
        engagementActions: [
          "Run a Story Giveaway: \"Tag a friend who could use clarity + follow us to win a free discovery call!\"",
          "DM all new followers: \"Welcome! Here's our free guide to preparing for a second opinion.\"",
          "Engage with 5 medical nonprofit accounts"
        ]
      },
      {
        day: "Friday",
        hasPost: true,
        postTitle: "Post 12 (Reel)",
        engagementActions: [
          "Ask in comments: \"What would you tell someone hesitant about a second opinion?\"",
          "Story Sticker: \"Quiz\" — \"CADENCE serves: A) Patients B) Families C) Clinicians D) All of the above\"",
          "Engage with every single comment — this is your conversion post!"
        ]
      },
      {
        day: "Saturday/Sunday",
        hasPost: false,
        postTitle: "Nurture & Reflect",
        engagementActions: [
          "Share a \"30-Day Recap\" in Stories (top posts, stats, thank you)",
          "DM 10 most engaged followers a thank-you note + discount code",
          "Plan next month's content based on engagement insights"
        ]
      }
    ]
  }
};

const dmTemplates = [
  {
    title: "For New Followers",
    template: "Hi [Name], thanks for following CADENCE Medical! We provide expert second opinions for complex diagnoses. Here's a free guide to navigating your health journey: [Link]. Let us know if you have any questions!"
  },
  {
    title: "For Engaged Followers",
    template: "Hi [Name], we noticed your comment on our post about [topic]. We really appreciate your engagement! We'd love to hear more about your story/interest. Is there anything we can help clarify?"
  },
  {
    title: "For Potential Clients",
    template: "Hi [Name], thanks for your interest in CADENCE. I see you're exploring second opinions — would you like to schedule a complimentary 15-minute discovery call to see if we're a good fit? You can book here: [Link]."
  }
];

const proTips = [
  { icon: "Users", text: "Use Name Mentions: When replying, use their first name." },
  { icon: "Heart", text: "Be Helpful, Not Salesy: Offer value first." },
  { icon: "MessageCircle", text: "Save DM Templates: But personalize each one." },
  { icon: "TrendingUp", text: "Track Engagement: Note which posts/comments get the most replies." },
  { icon: "Clock", text: "Stay Consistent: 15–20 minutes, 2x/day (morning/evening)." }
];

const AdminMediaAgenda = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [agenda, setAgenda] = useState(null);
  const [completedTasks, setCompletedTasks] = useState({});
  const [showDMTemplates, setShowDMTemplates] = useState(false);
  const [showProTips, setShowProTips] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  // Load agenda and completed tasks from localStorage/backend
  useEffect(() => {
    const loadAgenda = async () => {
      try {
        // Try to load from backend first
        const response = await fetch(`${API_URL}/api/admin/media-agenda`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setAgenda(data.agenda || defaultAgendaTemplate);
          setCompletedTasks(data.completed_tasks || {});
        } else {
          // Fallback to default template
          setAgenda(defaultAgendaTemplate);
          // Load completed tasks from localStorage
          const savedTasks = localStorage.getItem('cadence_media_completed');
          if (savedTasks) {
            setCompletedTasks(JSON.parse(savedTasks));
          }
        }
      } catch (err) {
        console.log('Using default agenda template');
        setAgenda(defaultAgendaTemplate);
        const savedTasks = localStorage.getItem('cadence_media_completed');
        if (savedTasks) {
          setCompletedTasks(JSON.parse(savedTasks));
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadAgenda();
  }, [token]);

  // Save completed tasks
  const toggleTaskComplete = async (weekKey, dayIndex, taskIndex) => {
    const taskKey = `${weekKey}-${dayIndex}-${taskIndex}`;
    const newCompleted = { ...completedTasks, [taskKey]: !completedTasks[taskKey] };
    setCompletedTasks(newCompleted);
    
    // Save to localStorage as backup
    localStorage.setItem('cadence_media_completed', JSON.stringify(newCompleted));
    
    // Try to save to backend
    try {
      await fetch(`${API_URL}/api/admin/media-agenda/progress`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ completed_tasks: newCompleted })
      });
    } catch (err) {
      console.log('Saved locally only');
    }
  };

  const getWeekProgress = (weekKey) => {
    if (!agenda) return { completed: 0, total: 0 };
    
    const week = agenda[weekKey];
    if (!week) return { completed: 0, total: 0 };
    
    let total = 0;
    let completed = 0;
    
    week.days.forEach((day, dayIndex) => {
      day.engagementActions.forEach((_, taskIndex) => {
        total++;
        if (completedTasks[`${weekKey}-${dayIndex}-${taskIndex}`]) {
          completed++;
        }
      });
    });
    
    return { completed, total };
  };

  const getCurrentWeekKey = () => `week${currentWeek}`;

  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-loading">
          <div className="spin"><CalendarIcon size={32} /></div>
          <p>Loading media agenda...</p>
        </div>
      </div>
    );
  }

  const weekKeys = ['week1', 'week2', 'week3', 'week4'];
  const currentWeekData = agenda[getCurrentWeekKey()];
  const progress = getWeekProgress(getCurrentWeekKey());

  return (
    <div className="admin-page-wrapper">
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-content">
            <button onClick={() => navigate('/admin')} className="back-btn">
              <ChevronLeft size={20} />
              Back
            </button>
            <h1>Media Management Agenda</h1>
            <p className="admin-subtitle">Weekly engagement prompts & daily actions for social media growth</p>
          </div>
          <div className="admin-header-actions">
            <button 
              onClick={() => setShowProTips(true)} 
              className="admin-btn secondary"
            >
              <Target size={16} />
              Pro Tips
            </button>
            <button 
              onClick={() => setShowDMTemplates(true)} 
              className="admin-btn primary"
            >
              <MessageCircle size={16} />
              DM Templates
            </button>
          </div>
        </div>

      {/* Progress Overview */}
      <div className="media-progress-grid">
        {weekKeys.map((weekKey, idx) => {
          const weekProgress = getWeekProgress(weekKey);
          const isActive = currentWeek === idx + 1;
          const percentage = weekProgress.total > 0 
            ? Math.round((weekProgress.completed / weekProgress.total) * 100) 
            : 0;
          
          return (
            <div 
              key={weekKey}
              className={`media-progress-card ${isActive ? 'active' : ''}`}
              onClick={() => setCurrentWeek(idx + 1)}
              data-testid={`week-${idx + 1}-card`}
            >
              <div className="progress-week-num">Week {idx + 1}</div>
              <div className="progress-bar-container">
                <div 
                  className="progress-bar-fill"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <div className="progress-stats">
                <span>{weekProgress.completed}/{weekProgress.total} tasks</span>
                <span className="progress-percent">{percentage}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Week Navigation */}
      <div className="media-week-nav">
        <button 
          onClick={() => setCurrentWeek(Math.max(1, currentWeek - 1))}
          disabled={currentWeek === 1}
          className="nav-btn"
        >
          <ChevronLeft size={20} />
        </button>
        <h2 className="week-title">{currentWeekData?.title}</h2>
        <button 
          onClick={() => setCurrentWeek(Math.min(4, currentWeek + 1))}
          disabled={currentWeek === 4}
          className="nav-btn"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Daily Agenda Cards */}
      <div className="media-days-grid">
        {currentWeekData?.days.map((day, dayIndex) => (
          <div 
            key={dayIndex} 
            className={`media-day-card ${day.hasPost ? 'has-post' : ''}`}
            data-testid={`day-card-${dayIndex}`}
          >
            <div className="day-header">
              <div className="day-info">
                <span className="day-name">{day.day}</span>
                {day.hasPost ? (
                  <span className="post-badge">
                    <Instagram size={14} />
                    {day.postTitle}
                  </span>
                ) : (
                  <span className="no-post-badge">No Post</span>
                )}
              </div>
              {day.postTitle && !day.hasPost && (
                <span className="focus-badge">{day.postTitle}</span>
              )}
            </div>
            
            <div className="engagement-actions">
              <h4>Engagement Actions:</h4>
              <ul className="action-list">
                {day.engagementActions.map((action, taskIndex) => {
                  const taskKey = `${getCurrentWeekKey()}-${dayIndex}-${taskIndex}`;
                  const isCompleted = completedTasks[taskKey];
                  
                  return (
                    <li 
                      key={taskIndex}
                      className={`action-item ${isCompleted ? 'completed' : ''}`}
                      onClick={() => toggleTaskComplete(getCurrentWeekKey(), dayIndex, taskIndex)}
                      data-testid={`task-${dayIndex}-${taskIndex}`}
                    >
                      <span className="checkbox">
                        {isCompleted ? (
                          <CheckCircle size={18} className="checked" />
                        ) : (
                          <Circle size={18} />
                        )}
                      </span>
                      <span className="action-text">{action}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* DM Templates Modal */}
      {showDMTemplates && (
        <div className="modal-overlay" onClick={() => setShowDMTemplates(false)}>
          <div className="modal-content dm-templates-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>DM Templates</h3>
              <button onClick={() => setShowDMTemplates(false)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {dmTemplates.map((template, idx) => (
                <div key={idx} className="dm-template-card">
                  <h4>{template.title}</h4>
                  <p className="template-text">{template.template}</p>
                  <button 
                    className="copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(template.template);
                      alert('Copied to clipboard!');
                    }}
                  >
                    Copy
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pro Tips Modal */}
      {showProTips && (
        <div className="modal-overlay" onClick={() => setShowProTips(false)}>
          <div className="modal-content tips-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Pro Tips for Engagement</h3>
              <button onClick={() => setShowProTips(false)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="tips-list">
                {proTips.map((tip, idx) => (
                  <div key={idx} className="tip-item">
                    <span className="tip-number">{idx + 1}</span>
                    <span className="tip-text">{tip.text}</span>
                  </div>
                ))}
              </div>
              <div className="time-recommendation">
                <Clock size={20} />
                <div>
                  <strong>Daily Time Investment</strong>
                  <p>15-20 minutes, 2x/day (morning/evening)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminMediaAgenda;
